"use client";

import { useEffect, useState } from "react";

const IST = new Intl.DateTimeFormat("en-GB", {
  timeZone: "Asia/Kolkata",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
});

/**
 * Always New Delhi (IST). Independent of the viewer's timezone (forced via
 * `Asia/Kolkata`) and of their device clock: the instant is anchored to the
 * server's clock from /api/now, then advanced with a monotonic timer. Device
 * time is only a last-resort fallback if that request fails.
 */
export function LocalTime() {
  const [now, setNow] = useState<number | null>(null);

  useEffect(() => {
    let intervalId: ReturnType<typeof setInterval> | undefined;
    let started = false;
    let cancelled = false;

    const start = (anchor: number) => {
      if (started || cancelled) return;
      started = true;
      const base = performance.now();
      const tick = () => setNow(anchor + (performance.now() - base));
      tick();
      intervalId = setInterval(tick, 1000);
    };

    fetch("/api/now", { cache: "no-store" })
      .then((r) => r.json() as Promise<{ now: number }>)
      .then((d) => start(d.now))
      .catch(() => start(Date.now()));

    // don't sit blank forever if the request hangs
    const fallback = setTimeout(() => start(Date.now()), 3000);

    return () => {
      cancelled = true;
      clearTimeout(fallback);
      if (intervalId) clearInterval(intervalId);
    };
  }, []);

  if (now === null) {
    return <div className="w-[200px]" />; // avoid layout shift
  }

  return (
    <div className="flex items-center gap-1.5 font-head text-xs tracking-tight">
      <span className="font-medium text-fg">{IST.format(now)}</span>
      <span className="text-fg-muted">Delhi, India</span>
    </div>
  );
}
